import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

import { Label, LabelSection, LabelToggle, MasterSourceSelect, Section, Separator, ToggleCard } from '@Components';
import { Input } from '@Components/ui-v2/input';
import { ConfigTargetInventory } from '@Models';

interface TargetInventorySectionProps {
  value: ConfigTargetInventory;
  onChange: (value: ConfigTargetInventory) => void;
  className?: string;
  readOnly?: boolean;
  connector?: string;
}

const TargetInventorySection = ({
  value,
  onChange,
  className,
  readOnly = true,
  connector,
}: TargetInventorySectionProps) => {
  const { t } = useTranslation();
  const [optionType] = useState<'Units' | 'DaysOnHand'>(
    value.ProvidedConfig?.UserConfig?.UnitsOption ? 'Units' : 'DaysOnHand',
  );

  const getCustomerMessageKey = (connectorType?: string): string => {
    if (!connectorType) {
      return 'ConfigurationPolicy.TargetInventory.CustomerMessage';
    }

    if (['Target', 'Walmart', 'Farmatodo'].includes(connectorType)) {
      return `ConfigurationPolicy.TargetInventory.CustomerMessage${connectorType}`;
    }

    return 'ConfigurationPolicy.TargetInventory.CustomerMessage';
  };

  const isCalculationTypeSelected = (type: 'BopsOptimal' | 'Provided') => {
    return value.CalculationType.includes(type);
  };

  const isBopsOptimalSelected = isCalculationTypeSelected('BopsOptimal') && value.CalculationType.length === 1;

  const areBothSelected = () => {
    return value.CalculationType.length > 1;
  };

  const handleServiceLevelChange = (level: number) => {
    const constrained = Math.max(0, Math.min(100, level));
    onChange({
      ...value,
      ServiceLevel: constrained,
    });
  };

  const masterSourceConfig = {
    DaysOnHand: {
      MasterSource: value.ProvidedConfig?.UserConfig?.DaysOnHandOption?.MasterSource,
      MasterColumnName: value.ProvidedConfig?.UserConfig?.DaysOnHandOption?.MasterColumnName,
      DefaultValue: value.ProvidedConfig?.UserConfig?.DaysOnHandOption?.DefaultValue,
    },
    Units: {
      MasterSource: value.ProvidedConfig?.UserConfig?.UnitsOption?.MasterSource,
      MasterColumnName: value.ProvidedConfig?.UserConfig?.UnitsOption?.MasterColumnName,
      DefaultValue: value.ProvidedConfig?.UserConfig?.UnitsOption?.DefaultValue,
    },
  };

  const unitsOptions = [
    {
      id: 'Target',
      label: t('ConfigurationPolicy.TargetInventory.Target'),
      description: t('ConfigurationPolicy.TargetInventory.TargetDesc'),
      tooltip: t('ConfigurationPolicy.TargetInventory.TargetTooltip'),
    },
    {
      id: 'ReorderPoint',
      label: t('ConfigurationPolicy.TargetInventory.ReorderPoint'),
      description: t('ConfigurationPolicy.TargetInventory.ReorderPointDesc'),
      tooltip: t('ConfigurationPolicy.TargetInventory.ReorderPointTooltip'),
    },
    {
      id: 'SafetyStock',
      label: t('ConfigurationPolicy.TargetInventory.SafetyStock'),
      description: t('ConfigurationPolicy.TargetInventory.SafetyStockDesc'),
      tooltip: t('ConfigurationPolicy.TargetInventory.SafetyStockTooltip'),
    },
  ];

  return (
    <div className={cn('space-y-6 mt-10', className)}>
      <Section
        left={
          <LabelSection
            label={t('ConfigurationPolicy.TargetInventory.CalculationType')}
            description={t('ConfigurationPolicy.TargetInventory.CalculationTypeDesc')}
          />
        }
        right={
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ToggleCard selected={isCalculationTypeSelected('BopsOptimal')} readOnly={readOnly} multiple>
                <div className="flex flex-col">
                  <div className="flex items-center space-x-2">
                    <LabelToggle label={t('ConfigurationPolicy.TargetInventory.BopsOptimalInventory')} />
                  </div>
                  <p
                    className={cn(
                      'text-sm text-brand-300',
                      isCalculationTypeSelected('BopsOptimal') ? 'text-brand-500' : 'text-gray-700',
                    )}>
                    {t('ConfigurationPolicy.TargetInventory.BopsOptimalDesc')}
                  </p>
                </div>
              </ToggleCard>
              <ToggleCard selected={isCalculationTypeSelected('Provided')} readOnly={readOnly} multiple>
                <div className="flex flex-col">
                  <div className="flex items-center space-x-2">
                    <LabelToggle label={t('ConfigurationPolicy.TargetInventory.Provided')} />
                  </div>
                  <p
                    className={cn(
                      'text-sm text-brand-300',
                      isCalculationTypeSelected('Provided') ? 'text-brand-500' : 'text-gray-700',
                    )}>
                    {t('ConfigurationPolicy.TargetInventory.ProvidedDesc')}
                  </p>
                </div>
              </ToggleCard>
            </div>
            {areBothSelected() && (
              <div className="flex flex-col mt-2 w-full">
                <Label>{t('ConfigurationPolicy.TargetInventory.DefaultCalculationType')}</Label>
                <div className="flex items-center space-x-2 border border-solid border-gray-200 rounded-lg p-2 mt-3">
                  <span className="text-gray-500 text-[15px]">
                    {value.DefaultCalculationType === 'BopsOptimal'
                      ? t('ConfigurationPolicy.TargetInventory.BopsOptimalInventory')
                      : t('ConfigurationPolicy.TargetInventory.Provided')}
                  </span>
                </div>
              </div>
            )}
          </div>
        }
      />

      {!isBopsOptimalSelected && <Separator orientation="horizontal" className="w-full bg-gray-300 h-[1px]" />}

      {isCalculationTypeSelected('Provided') && value.ProvidedConfig && (
        <>
          <Section
            left={
              <LabelSection
                label={t('ConfigurationPolicy.TargetInventory.Source')}
                description={t('ConfigurationPolicy.TargetInventory.SourceDesc')}
              />
            }
            right={
              <div className="space-y-4 w-full">
                <div className="space-y-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <ToggleCard selected={value.ProvidedConfig.Source === 'User'} readOnly={readOnly}>
                      <div className="flex flex-col">
                        <div className="flex items-center space-x-2">
                          <LabelToggle label={t('ConfigurationPolicy.TargetInventory.User')} />
                        </div>
                        <p
                          className={cn(
                            'text-sm text-brand-300 mt-1',
                            value.ProvidedConfig.Source === 'User' ? 'text-brand-500' : 'text-gray-700',
                          )}>
                          {t('ConfigurationPolicy.TargetInventory.UserTooltip')}
                        </p>
                      </div>
                    </ToggleCard>
                    <ToggleCard selected={value.ProvidedConfig.Source === 'Customer'} readOnly={readOnly}>
                      <div className="flex flex-col">
                        <div className="flex items-center space-x-2">
                          <LabelToggle label={t('ConfigurationPolicy.TargetInventory.Customer')} />
                        </div>
                        <p
                          className={cn(
                            'text-sm text-brand-300 mt-1',
                            value.ProvidedConfig.Source === 'Customer' ? 'text-brand-500' : 'text-gray-700',
                          )}>
                          {t('ConfigurationPolicy.TargetInventory.CustomerTooltip')}
                        </p>
                      </div>
                    </ToggleCard>
                  </div>
                </div>
                {value.ProvidedConfig.Source === 'Customer' && (
                  <div className="p-4 border rounded-lg animate-slide-in border-1 border-gray-200 border-solid">
                    <p className="text-sm text-gray-500">{t(getCustomerMessageKey(connector))}</p>
                  </div>
                )}
              </div>
            }
          />

          {value.ProvidedConfig.Source === 'User' && value.ProvidedConfig.UserConfig && (
            <>
              <Separator orientation="horizontal" className="w-full bg-gray-300 h-[1px]" />
              <Section
                left={
                  <LabelSection
                    label={t('ConfigurationPolicy.TargetInventory.ConfigurationType')}
                    description={t('ConfigurationPolicy.TargetInventory.ConfigurationTypeDesc')}
                  />
                }
                right={
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 !mt-[12px]">
                    <ToggleCard selected={optionType === 'Units'} readOnly={readOnly}>
                      <div className="flex flex-col">
                        <div className="flex items-center space-x-2">
                          <LabelToggle label={t('ConfigurationPolicy.TargetInventory.Units')} />
                        </div>
                        <p
                          className={cn(
                            'text-sm text-brand-300 mt-1',
                            optionType === 'Units' ? 'text-brand-500' : 'text-gray-700',
                          )}>
                          {t('ConfigurationPolicy.TargetInventory.UnitsTooltip')}
                        </p>
                      </div>
                    </ToggleCard>
                    <ToggleCard selected={optionType === 'DaysOnHand'} readOnly={readOnly}>
                      <div className="flex flex-col">
                        <div className="flex items-center space-x-2">
                          <LabelToggle label={t('ConfigurationPolicy.TargetInventory.DaysOnHand')} />
                        </div>
                        <p
                          className={cn(
                            'text-sm text-brand-300 mt-1',
                            optionType === 'DaysOnHand' ? 'text-brand-500' : 'text-gray-700',
                          )}>
                          {t('ConfigurationPolicy.TargetInventory.DaysOnHandTooltip')}
                        </p>
                      </div>
                    </ToggleCard>
                  </div>
                }
              />
              <Separator orientation="horizontal" className="w-full bg-gray-300 h-[1px]" />

              {optionType === 'Units' && value.ProvidedConfig.UserConfig.UnitsOption && (
                <Section
                  left={
                    <LabelSection
                      label={t('ConfigurationPolicy.TargetInventory.UnitsType')}
                      description={t('ConfigurationPolicy.TargetInventory.UnitsTypeDesc')}
                    />
                  }
                  right={
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {unitsOptions.map((option) => (
                          <div key={option.id}>
                            <ToggleCard
                              selected={value.ProvidedConfig?.UserConfig?.UnitsOption?.Type === option.id}
                              readOnly={readOnly}>
                              <div className="flex flex-col">
                                <div className="flex items-center space-x-2">
                                  <LabelToggle label={option.label} />
                                </div>
                                <p
                                  className={cn(
                                    'text-sm text-brand-300 mt-1',
                                    value.ProvidedConfig?.UserConfig?.UnitsOption?.Type === option.id
                                      ? 'text-brand-500'
                                      : 'text-gray-700',
                                  )}>
                                  {option.tooltip}
                                </p>
                              </div>
                            </ToggleCard>
                          </div>
                        ))}
                      </div>
                    </div>
                  }
                />
              )}

              {optionType === 'DaysOnHand' && value.ProvidedConfig.UserConfig.DaysOnHandOption && (
                <Section
                  left={
                    <LabelSection
                      label={t('ConfigurationPolicy.TargetInventory.DaysOnHandDirection')}
                      description={t('ConfigurationPolicy.TargetInventory.DaysOnHandDirectionDesc')}
                    />
                  }
                  right={
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                        <ToggleCard
                          selected={value.ProvidedConfig.UserConfig.DaysOnHandOption.Type === 'Backward'}
                          readOnly={readOnly}>
                          <div className="flex flex-col">
                            <div className="flex items-center space-x-2">
                              <LabelToggle label={t('ConfigurationPolicy.TargetInventory.Backward')} />
                            </div>
                            <p
                              className={cn(
                                'text-sm text-brand-300 mt-1',
                                value.ProvidedConfig.UserConfig.DaysOnHandOption.Type === 'Backward'
                                  ? 'text-brand-500'
                                  : 'text-gray-700',
                              )}>
                              {t('ConfigurationPolicy.TargetInventory.BackwardDesc')}
                            </p>
                          </div>
                        </ToggleCard>
                        <ToggleCard
                          selected={value.ProvidedConfig.UserConfig.DaysOnHandOption.Type === 'Forward'}
                          readOnly={readOnly}>
                          <div className="flex flex-col">
                            <div className="flex items-center space-x-2">
                              <LabelToggle label={t('ConfigurationPolicy.TargetInventory.Forward')} />
                            </div>
                            <p
                              className={cn(
                                'text-sm text-brand-300 mt-1',
                                value.ProvidedConfig.UserConfig.DaysOnHandOption.Type === 'Forward'
                                  ? 'text-brand-500'
                                  : 'text-gray-700',
                              )}>
                              {t('ConfigurationPolicy.TargetInventory.ForwardDesc')}
                            </p>
                          </div>
                        </ToggleCard>
                      </div>
                    </div>
                  }
                />
              )}

              <Separator orientation="horizontal" className="w-full bg-gray-300 h-[1px]" />

              {(value.ProvidedConfig.UserConfig.DaysOnHandOption || value.ProvidedConfig.UserConfig.UnitsOption) && (
                <Section
                  left={
                    <LabelSection
                      label={t('ConfigurationPolicy.TargetInventory.DataSource')}
                      description={t('ConfigurationPolicy.TargetInventory.DataSourceDesc')}
                    />
                  }
                  right={
                    <div className="space-y-4">
                      <MasterSourceSelect
                        value={{
                          MasterSource: masterSourceConfig?.[optionType]?.MasterSource ?? 'ProductMaster',
                          MasterColumnName: masterSourceConfig?.[optionType]?.MasterColumnName,
                          DefaultValue: masterSourceConfig?.[optionType]?.DefaultValue ?? 7,
                        }}
                        onChange={(masterSourceValue) => {
                          const configType = optionType === 'DaysOnHand' ? 'DaysOnHandOption' : 'UnitsOption';
                          if (!value.ProvidedConfig?.UserConfig?.[configType]) return;

                          onChange({
                            ...value,
                            ProvidedConfig: {
                              ...value.ProvidedConfig,
                              UserConfig: {
                                ...value.ProvidedConfig.UserConfig,
                                [configType]: {
                                  ...value.ProvidedConfig.UserConfig[configType],
                                  MasterSource: masterSourceValue.MasterSource,
                                  MasterColumnName: masterSourceValue.MasterColumnName,
                                  DefaultValue: masterSourceValue.DefaultValue,
                                },
                              },
                            },
                          });
                        }}
                        readOnly={readOnly}
                        defaultLabel={t('ConfigurationPolicy.TargetInventory.DefaultValue', {
                          type:
                            optionType === 'DaysOnHand'
                              ? t('ConfigurationPolicy.TargetInventory.Days')
                              : t('ConfigurationPolicy.TargetInventory.Units'),
                        })}
                      />
                    </div>
                  }
                />
              )}
            </>
          )}
        </>
      )}

      <Separator orientation="horizontal" className="w-full bg-gray-300 h-[1px]" />
      <Section
        left={
          <LabelSection
            label={t('ConfigurationPolicy.TargetInventory.ServiceLevel')}
            description={t('ConfigurationPolicy.TargetInventory.ServiceLevelDesc')}
          />
        }
        right={
          <div className="space-y-3.5">
            <div className="flex items-center gap-2">
              <Label>{t('ConfigurationPolicy.TargetInventory.ServiceLevelPercent')}</Label>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={0}
                max={100}
                value={value.ServiceLevel}
                readOnly={readOnly}
                onChange={(e) => handleServiceLevelChange(Number(e.target.value))}
                className="bg-white"
              />
            </div>
          </div>
        }
      />
    </div>
  );
};

export default TargetInventorySection;
